import React from 'react';

/**
 * Utilidades para extraer y procesar referencias a documentos en el texto del agente
 */

/**
 * Extrae IDs de documentos del texto usando el patrón ID: [uuid] o Documento: [uuid]
 * @param text - Texto donde buscar referencias
 * @returns Array de UUIDs encontrados
 */
const UUID_CAPTURE = '([a-f0-9]{8}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{12})';

export function extractDocumentIds(text: string): string[] {
  const ids: string[] = [];
  const add = (id: string) => {
    if (!ids.includes(id)) ids.push(id);
  };

  let match: RegExpExecArray | null;

  const kbPattern = new RegExp(`ID:\\s*kb-${UUID_CAPTURE}`, 'gi');
  while ((match = kbPattern.exec(text)) !== null) {
    add(`kb-${match[1]}`);
  }

  const legacyPattern = new RegExp(`(?:ID:\\s*|Documento:\\s*)${UUID_CAPTURE}`, 'gi');
  while ((match = legacyPattern.exec(text)) !== null) {
    if (/ID:\s*kb-/i.test(match[0])) continue;
    add(match[1]);
  }

  /** Web chat / add-in: @[label](document:uuid), knowledge_base, category, etc. */
  const mentionPattern =
    /@\[([^\]]*)\]\(([^:]+):([^:)]+)(?::(word|excel))?\)/gi;
  while ((match = mentionPattern.exec(text)) !== null) {
    const mentionType = match[2];
    const id = match[3];
    if (mentionType === 'execution_template') continue;
    if (mentionType === 'document' || mentionType === 'category') {
      add(id);
    } else if (mentionType === 'knowledge_base') {
      add(id.startsWith('kb-') ? id : `kb-${id}`);
    } else if (mentionType === 'folder') {
      add(id.startsWith('kb-') ? id : `kb-${id}`);
    }
  }

  return ids;
}

/**
 * Tipo para información de documento
 */
export interface DocumentInfo {
  id: string;
  name: string;
  type?: string;
  url?: string;
}

/**
 * Tipo para información de imagen
 */
export interface ImageInfo {
  url: string;
  alt?: string;
}

// Helper: sanitize image urls that may include trailing punctuation from inline text (e.g. ending '.' or ',' or ')')
function sanitizeImageUrl(url: string): string {
  if (!url) return url;
  // Trim common trailing punctuation that often gets included when copying from text
  // This is a simple heuristic and preserves query strings and most valid chars
  return url.replace(/[\)\.,;:]+$/g, '');
}

/**
 * Extrae URLs de imágenes del texto. Soporta:
 * - Markdown images: ![alt](url)
 * - Plain image URLs ending with common extensions
 * - Prefixed forms: Image: url, Foto: url
 */
export function extractImageUrls(text: string): ImageInfo[] {
  const urls: ImageInfo[] = [];
  const seen = new Set<string>();
  if (!text) return urls;

  // 1) Markdown image syntax - allow parentheses inside URLs and use non-greedy match
  const markdownPattern = /!\[([^\]]*)\]\((https?:\/\/[^\s]+?)\)/gi;
  let match: RegExpExecArray | null;

  while ((match = markdownPattern.exec(text)) !== null) {
    const alt = match[1] || undefined;
    const url = match[2];
    if (!seen.has(url)) {
      seen.add(url);
      urls.push({ url, alt });
    }
  }

  // 2) Prefixed forms like "Image: <url>" or "Foto: <url>" - allow parentheses in URL
  const prefixedPattern = /(?:Image|Foto):\s*(https?:\/\/[^\s]+)/gi;
  while ((match = prefixedPattern.exec(text)) !== null) {
    const url = match[1];
    if (!seen.has(url)) {
      seen.add(url);
      urls.push({ url });
    }
  }

  // 3) Plain URLs with image extensions - allow parentheses and use non-greedy match
  const extPattern = /(https?:\/\/[^\s]+?\.(?:png|jpe?g|gif|webp|svg)(?:\?[^\s]*)?)/gi;
  while ((match = extPattern.exec(text)) !== null) {
    const url = match[1];
    if (!seen.has(url)) {
      seen.add(url);
      urls.push({ url });
    }
  }

  return urls;
}

/**
 * Procesa el texto reemplazando referencias a imágenes con nodos React (renderImage)
 */
export function processImageReferences(
  text: string,
  renderImage: (url: string, alt?: string, matchIndex?: number) => React.ReactNode
): React.ReactNode[] {
  if (!renderImage) return [text];
  const parts: React.ReactNode[] = [];
  let lastIndex = 0;
  let matchIndex = 0;

  const pattern = /!\[([^\]]*)\]\((https?:\/\/[^\s]+?)\)|(?:Image|Foto):\s*(https?:\/\/[^\s]+)|(https?:\/\/[^\s]+?\.(?:png|jpe?g|gif|webp|svg)(?:\?[^\s]*)?)/gi;
  let match: RegExpExecArray | null;

  while ((match = pattern.exec(text)) !== null) {
    if (match.index > lastIndex) {
      parts.push(text.substring(lastIndex, match.index));
    }

    let url: string | undefined;
    let alt: string | undefined;

    if (match[2]) {
      url = match[2];
      alt = match[1] || undefined;
    } else if (match[3]) {
      url = match[3];
    } else if (match[4]) {
      url = match[4];
    }

    if (url) {
      const sanitizedUrl = sanitizeImageUrl(url);
      if (sanitizedUrl !== url) {
        console.debug('processImageReferences: sanitized image url', url, '=>', sanitizedUrl);
        url = sanitizedUrl;
      }
      parts.push(renderImage(url, alt, matchIndex));
    } else {
      parts.push(text.substring(match.index, pattern.lastIndex));
    }

    lastIndex = pattern.lastIndex;
    matchIndex++;
  }

  if (lastIndex < text.length) {
    parts.push(text.substring(lastIndex));
  }

  return parts.length > 0 ? parts : [text];
}

/**
 * Procesa el texto y reemplaza las referencias a documentos con componentes React
 * @param text - Texto a procesar
 * @param documents - Array de documentos con información
 * @param renderBadge - Función para renderizar el badge del documento (recibe también el índice del match)
 * @returns Array de nodos React
 */
export function processDocumentReferences(
  text: string,
  documents?: DocumentInfo[],
  renderBadge?: (docId: string, docName: string, docType?: string, docUrl?: string, matchIndex?: number, page?: number) => React.ReactNode
): React.ReactNode[] {
  if (!renderBadge) {
    return [text];
  }

  const docs = documents ?? [];
  const uuid = '[a-f0-9]{8}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{12}';
  const kbId = '(?:kb-)?[a-f0-9]{8}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{12}';
  const pKb = `(?:ID:\\s*|Documento:\\s*)kb-(${uuid})`;
  const pDoc = `(?:ID:\\s*|Documento:\\s*)(${uuid})`;
  const pEt = `@\\[([^\\]]*)\\]\\(execution_template:(${uuid})(?::(word|excel))?\\)`;
  const pAt = `@\\[([^\\]]*)\\]\\((?!execution_template:)([^:]+):(${kbId}|${uuid})\\)(?:\\(page:(\\d+)\\))?`;
  const pattern = new RegExp(`${pKb}|${pDoc}|${pEt}|${pAt}`, 'gi');

  const parts: React.ReactNode[] = [];
  let lastIndex = 0;
  let match: RegExpExecArray | null;
  let matchIndex = 0;

  while ((match = pattern.exec(text)) !== null) {
    if (match.index > lastIndex) {
      parts.push(text.substring(lastIndex, match.index));
    }

    const matchLength = match[0].length;

    if (match[1]) {
      const docId = `kb-${match[1]}`;
      const document = docs.find((d) => d.id === docId);
      parts.push(
        renderBadge(
          docId,
          document?.name || `KB ${docId}`,
          document?.type,
          document?.url,
          matchIndex,
          undefined
        )
      );
    } else if (match[2]) {
      const docId = match[2];
      const document = docs.find((d) => d.id === docId);
      parts.push(
        renderBadge(
          docId,
          document?.name || `Document ${docId.substring(0, 8)}…`,
          document?.type,
          document?.url,
          matchIndex,
          undefined
        )
      );
    } else if (match[4]) {
      const etLabel = match[3]?.trim() || 'Template';
      const etId = match[4];
      parts.push(
        renderBadge(etId, etLabel, 'execution_template', undefined, matchIndex, undefined)
      );
    } else if (match[8]) {
      const label = match[6]?.trim() || '';
      const docId = match[8];
      const page = match[9] ? parseInt(match[9], 10) : undefined;
      const document = docs.find((d) => d.id === docId);
      parts.push(
        renderBadge(
          docId,
          document?.name || label || `Document ${docId.substring(0, 8)}…`,
          document?.type,
          document?.url,
          matchIndex,
          page
        )
      );
    }

    lastIndex = match.index + matchLength;
    matchIndex++;
  }

  // Añadir texto restante
  if (lastIndex < text.length) {
    parts.push(text.substring(lastIndex));
  }

  return parts.length > 0 ? parts : [text];
}
